import React, { FC } from "react";
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { ViewsMenuSettingsStyles } from "./viewsMenuSettingsStyles";
import { useTranslation } from "react-i18next";
import { CURRENT_VIEW_VERSION } from "../../../../../../../constants/save-views";

interface SavedView {
  id: string;
  name: string;
  savedDate: string;
  filters?: any;
  configuration?: any;
  dataViews?: any[];
  createdAt?: string;
  version?: string;
}

interface SavedViewsListProps {
  views: SavedView[];
  onViewClick: (view: SavedView) => void;
  onViewDelete: (id: string) => void;
  disabled?: boolean;
}

const SavedViewsList: FC<SavedViewsListProps> = ({
  views,
  onViewClick,
  onViewDelete,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation("global");

  const currentViewVersion = CURRENT_VIEW_VERSION;

  const formatDateByLocale = (dateString: string) => {
    const date = new Date(dateString);
    const locale = i18n.language;

    const localeMap: { [key: string]: string } = {
      eng: "en-US",
      fr: "fr-FR",
      spanish: "es-ES",
      de: "de-DE",
    };

    const browserLocale = localeMap[locale] || locale || "en-US";

    return date.toLocaleDateString(browserLocale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  if (views.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={ViewsMenuSettingsStyles.noViewsText}
      >
        {t("Data_views.no_views")}
      </Typography>
    );
  }

  return (
    <List
      sx={{
        ...ViewsMenuSettingsStyles.viewsList,
        maxHeight: views.length > 3 ? "240px" : "auto", // ~80px per item * 3 = 240px
        overflowY: views.length > 3 ? "auto" : "visible",
        overflowX: "hidden",
      }}
    >
      {views
        .toSorted((a, b) => {
          const dateA = new Date(a.createdAt || a.savedDate);
          const dateB = new Date(b.createdAt || b.savedDate);
          return dateB.getTime() - dateA.getTime();
        })
        .map((view, index) => (
          <React.Fragment key={view.id}>
            {index > 0 && <Divider />}
            <Tooltip
              title={
                currentViewVersion !== view.version
                  ? t("Data.views.Not_compatible")
                  : ""
              }
            >
              <ListItem
                sx={{
                  ...ViewsMenuSettingsStyles.listItem,
                  ...(disabled
                    ? ViewsMenuSettingsStyles.listItemDisabled
                    : ViewsMenuSettingsStyles.listItemEnabled),
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDelete(view.id);
                    }}
                    sx={ViewsMenuSettingsStyles.deleteButton}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
                onClick={() => !disabled && onViewClick(view)}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      sx={
                        currentViewVersion !== view.version
                          ? ViewsMenuSettingsStyles.viewTitleDisabled
                          : ViewsMenuSettingsStyles.viewTitle
                      }
                    >
                      {view.name} <span>-</span>{" "}
                      <span
                        style={{ fontWeight: "normal", fontSize: "0.75em" }}
                      >
                        {formatDateByLocale(view.createdAt || view.savedDate)}
                      </span>
                    </Typography>
                  }
                />
              </ListItem>
            </Tooltip>
          </React.Fragment>
        ))}
    </List>
  );
};

export default SavedViewsList;
